import React, { useEffect, useRef, useState, useMemo, useContext } from 'react';
import '../HomeBody/HomeBody.css';
import './DropDown.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { FepkContext } from '../../context/FepkContext.js';
import GenderDropdown from './GenderDropdown.js';
import AgeRangeDropdown from './AgeRangeDropdown';
import EthnicityDropdown from './EthnicityDropdown';
import RepresentationDropdown from './RepresentationDropdown';
import CityDropdown from './CityDropdown';
import CountryDropdown from './CountryDropdown';
import { AGE_OPTIONS } from '../../constants/AgeOptions.js';
import { ETHNICITY_OPTIONS } from '../../constants/EthnicityOptions.js';
import { useTranslation } from 'react-i18next';

export default function FilterTag({ isActive }) {
  const { filterQuery, setFilterQuery } = useContext(FepkContext);
  const { t } = useTranslation();
  const containerRef = useRef(null);

  const getInitialValue = (category) => {
    if (!filterQuery || !Array.isArray(filterQuery)) return null;
    const match = filterQuery.find(f => f.startsWith(`${category}:`));
    return match ? match.split(': ')[1] : null;
  };

  const [selectedGender, setSelectedGender] = useState(() => getInitialValue('Gender'));
  const [selectedAgeRange, setSelectedAgeRange] = useState(() => getInitialValue('Age Range'));
  const [selectedEthnicity, setSelectedEthnicity] = useState(() => getInitialValue('Ethnicity'));
  const [selectedRepresentation, setSelectedRepresentation] = useState(() => getInitialValue('Representation'));
  const [selectedCity, setSelectedCity] = useState(() => getInitialValue('City'));
  const [selectedCountry, setSelectedCountry] = useState(() => getInitialValue('Country'));
  
  const [selectedDropdown, setSelectedDropdown] = useState(null);

  useEffect(() => {
    if (!Array.isArray(filterQuery)) return;

    const newValues = {
      Gender: null,
      'Age Range': null,
      Ethnicity: null,
      Representation: null,
      City: null,
      Country: null
    };

    filterQuery.forEach(filter => {
      const parts = filter.split(':');
      if (parts.length < 2) return;
      const name = parts[0].trim();
      const value = parts[1].trim();
      if (newValues.hasOwnProperty(name)) {
        newValues[name] = value;
      }
    });

    setSelectedGender(newValues.Gender);
    setSelectedAgeRange(newValues['Age Range']);
    setSelectedEthnicity(newValues.Ethnicity);
    setSelectedRepresentation(newValues.Representation);
    setSelectedCity(newValues.City);
    setSelectedCountry(newValues.Country);

  }, [filterQuery]);

  const handleDropdownSelection = (name, value) => {
    if (value === null) {
      setSelectedDropdown(prev => (prev === name ? null : name));
      return;
    }

    setFilterQuery((prev) => {
      let updatedQuery = prev.filter((item) => !item.startsWith(`${name}:`));
      if (value) updatedQuery.push(`${name}: ${value}`);
      return updatedQuery;
    });
    
    setSelectedDropdown(null);
  };

  const isFilmmakerView = isActive === 'filmmaker';
  const isPersonView = isActive === 'actor' || isFilmmakerView;
  const resetTagName = isFilmmakerView ? 'All Filmmakers' : 'All Actors';

  const personFilterTag = useMemo(() => {
    const tags = [
      { name: 'Gender', isActive: false },
      { name: 'Age Range', isActive: false },
      { name: 'Ethnicity', isActive: false },
    ];
    if (!isFilmmakerView) tags.push({ name: 'Representation', isActive: false });
    tags.push({ name: 'City', isActive: false }, { name: 'Country', isActive: false });
    tags.push({ name: resetTagName, isActive: true });
    return tags;
  }, [resetTagName, isFilmmakerView]);

  const [filterTags, setFilterTags] = useState(
    isPersonView ? personFilterTag : []
  );

  useEffect(() => {
    setFilterTags(isPersonView ? personFilterTag : []);
  }, [personFilterTag, isPersonView]);

  const clickHandler = (name, isCurrentlyActive) => {
    if (name === 'All Actors' || name === 'All Filmmakers') {
      setSelectedGender(null);
      setSelectedAgeRange(null);
      setSelectedEthnicity(null);
      setSelectedRepresentation(null);
      setSelectedCity(null);
      setSelectedCountry(null);
      setFilterQuery([]);
    } else {
      setFilterQuery(prev => 
        isCurrentlyActive ? prev.filter(i => i !== name) : [...prev, name]
      );
    }
  };

  const FilterButton = ({ name, isActive, selectedValue }) => {
    const isDropdownActive = selectedDropdown === name;
    const isDropdownType = ['Gender', 'Age Range', 'Ethnicity', 'Representation', 'City', 'Country'].includes(name);

    const buttonRef = useRef(null);

    useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownActive && buttonRef.current && !buttonRef.current.contains(event.target)) {
        setSelectedDropdown(null);
      }
    };

    if (isDropdownActive) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownActive]);

  const displayLabel = useMemo(() => {
    if (!selectedValue) return t(name);

    if (name === 'Age Range') {
      const found = AGE_OPTIONS.find(opt => String(opt.value) === String(selectedValue));
      return found ? found.label : t(name);
    }

    if (name === 'Ethnicity') {
      const found = ETHNICITY_OPTIONS.find(opt => opt.value === selectedValue);
      return found ? t(found.label) : t(name);
    }

    return selectedValue;
  }, [name, selectedValue]);

    return (
      <div>
        {isDropdownType ? (
          <div className="tw-relative tw-inline-block" ref={buttonRef}>
            <button
              className={`tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-1.5 tw-rounded-full tw-text-sm tw-font-medium tw-transition-colors tw-border tw-cursor-pointer tw-whitespace-nowrap ${
                selectedValue
                  ? "tw-bg-[#712CB0] tw-text-white tw-border-[#712CB0]"
                  : "tw-bg-transparent tw-text-white/60 tw-border-white/20 hover:tw-text-white hover:tw-border-white/40"
              }`}
              onClick={() => handleDropdownSelection(name, null)}
            >
              {displayLabel}
              <span className="tw-flex tw-items-center tw-gap-1">
                {selectedValue && (
                  <span
                    className="tw-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded-full tw-text-[10px] hover:tw-bg-white/20"
                    onClick={(e) => { e.stopPropagation(); handleDropdownSelection(name, ""); }}
                  >
                    ✕
                  </span>
                )}
                <FontAwesomeIcon icon={isDropdownActive ? faSortUp : faSortDown} className="tw-text-xs" />
              </span>
            </button>

            {isDropdownActive && (
              <div className="dropdown-options">
                {name === 'Gender' && <GenderDropdown selectedValue={selectedGender} onOptionSelect={(v) => handleDropdownSelection(name, v)} />}
                {name === 'Age Range' && <AgeRangeDropdown selectedValue={selectedAgeRange} onOptionSelect={(v) => handleDropdownSelection(name, v)} />}
                {name === 'Ethnicity' && <EthnicityDropdown selectedValue={selectedEthnicity} onOptionSelect={(v) => handleDropdownSelection(name, v)} />}
                {name === 'Representation' && <RepresentationDropdown selectedValue={selectedRepresentation} onOptionSelect={(v) => handleDropdownSelection(name, v)} />}
                {name === 'City' && <CityDropdown selectedValue={selectedCity} onOptionSelect={(v) => handleDropdownSelection(name, v)} />}
                {name === 'Country' && <CountryDropdown selectedValue={selectedCountry} onOptionSelect={(v) => handleDropdownSelection(name, v)} />}
              </div>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => clickHandler(name, isActive)}
            className={`tw-px-4 tw-py-1.5 tw-rounded-full tw-text-sm tw-font-medium tw-transition-colors tw-border tw-cursor-pointer tw-whitespace-nowrap ${
              isActive
                ? "tw-bg-[#712CB0] tw-text-white tw-border-[#712CB0]"
                : "tw-bg-transparent tw-text-white/60 tw-border-white/20 hover:tw-text-white hover:tw-border-white/40"
            }`}
          >
            {t(name)}
          </button>
        )}
      </div>
    );
  };

  return (
    <div ref={containerRef}>
      <div className="tw-flex tw-flex-wrap tw-justify-center tw-items-center tw-gap-2 tw-bg-[#1e0039] tw-py-4 tw-px-4">
        {filterTags.map((tag, index) => {
          const val = tag.name === 'Gender' ? selectedGender :
                      tag.name === 'Age Range' ? selectedAgeRange :
                      tag.name === 'Ethnicity' ? selectedEthnicity :
                      tag.name === 'Representation' ? selectedRepresentation :
                      tag.name === 'City' ? selectedCity :
                      tag.name === 'Country' ? selectedCountry : null;
          
          const isResetTag = tag.name === 'All Actors' || tag.name === 'All Filmmakers';
          const isAllActorsActive = isResetTag && !selectedGender && !selectedAgeRange && !selectedEthnicity && !selectedRepresentation && !selectedCity && !selectedCountry;

          return (
            <FilterButton
              key={index}
              name={tag.name}
              isActive={isResetTag ? isAllActorsActive : tag.isActive}
              selectedValue={val}
            />
          );
        })}
      </div>
    </div>
  );
}